import { useState, useEffect } from 'react';
import {
  Card, CardHeader, Input, Field, Table, THead, TBody, TR, TH, TD,
  EmptyState, ErrorNotice, Loading, Pagination, Button, Modal,
} from '../../components/ui/index.js';
import { useResource, useMutation } from '../../hooks/useApi.js';
import { useToast } from '../../components/ToastProvider.jsx';
import { useAuth } from '../../lib/auth.jsx';
import { can } from '../../lib/roles.js';
import { http } from '../../lib/api.js';
import { formatMoney, formatTimestamp, formatNumber } from '../../lib/format.js';
import { Users, UserPlus } from 'lucide-react';
import { PageHero } from '../../components/PageHero.jsx';

/**
 * Adding a customer before they have bought anything.
 *
 * Records normally appear from payments (CU-1), which covers the common case and
 * nothing else — a shop putting its regulars in before the first sale, or moving
 * a list off paper, had no way in at all.
 */
function NewCustomerDialog({ open, onClose, onCreated }) {
  const toast = useToast();
  const [form, setForm] = useState({ phone: '', email: '', name: '' });

  const { mutate, loading, error, reset } = useMutation(async () => {
    const customer = await http.post('/dashboard/customers', {
      ...(form.phone.trim() ? { phone: form.phone.trim() } : {}),
      ...(form.email.trim() ? { email: form.email.trim() } : {}),
      ...(form.name.trim() ? { name: form.name.trim() } : {}),
    });
    toast.success(`${customer.name || customer.phone || customer.email} added`);
    onCreated?.();
    close();
  });

  function close() {
    setForm({ phone: '', email: '', name: '' });
    reset();
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="Add a customer"
      blurb="A phone number or an email — one of them, so you can find them again."
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={loading}>Cancel</Button>
          <Button loading={loading} onClick={() => mutate().catch(() => {})}>Add customer</Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); mutate().catch(() => {}); }}>
        {error && <ErrorNotice error={error} title="Could not add them" />}

        <Field
          label="Phone"
          hint="How a customer is identified here — two payments with the same number are the same person."
          error={error?.fieldErrors?.phone}
        >
          {(props) => (
            <Input
              {...props}
              type="tel"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              placeholder="+91 98765 43210"
              autoFocus
            />
          )}
        </Field>

        <Field label="Email" hint="Optional. Used for receipts." error={error?.fieldErrors?.email}>
          {(props) => (
            <Input
              {...props}
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="them@example.com"
            />
          )}
        </Field>

        <Field label="Name">
          {(props) => (
            <Input
              {...props}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Asha Menon"
            />
          )}
        </Field>
      </form>
    </Modal>
  );
}

export function CustomersPage() {
  const { user } = useAuth();
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [skip, setSkip] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => { setApplied(search.trim()); setSkip(0); }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, error, loading, refetch } = useResource('/dashboard/customers', {
    params: { limit: 25, skip, ...(applied ? { search: applied } : {}) },
  });

  return (
    <>
      <PageHero
        icon={Users}
        chip="Workspace"
        title="Customers"
        actions={can(user?.role, 'MANAGE_CUSTOMERS')
          ? <Button icon={UserPlus} onClick={() => setAdding(true)}>Add customer</Button>
          : undefined}
        blurb="Built automatically from the payments you take — your customers never create an account."
      />

      <Card>
        <CardHeader
          title="All customers"
          blurb={data ? `${data.total} total` : undefined}
          actions={
            <div className="w-56">
              <Field>
                {(props) => (
                  <Input {...props} type="search" size="sm" value={search}
                    onChange={(e) => setSearch(e.target.value)} placeholder="Name, phone or email" />
                )}
              </Field>
            </div>
          }
        />

        {loading && <Loading label="Loading customers…" />}
        {error && <ErrorNotice error={error} onRetry={refetch} className="m-4" />}

        {!loading && !error && data?.customers?.length === 0 && (
          <EmptyState
            title={applied ? 'No customers match that search' : 'No customers yet'}
            blurb={
              applied
                ? 'Try a different name, phone number or email address.'
                : 'A customer record appears the first time you take a payment with a phone number or email on it.'
            }
          />
        )}

        {!loading && !error && data?.customers?.length > 0 && (
          <>
            <Table>
              <THead>
                <TR>
                  <TH>Customer</TH><TH align="right">Payments</TH><TH align="right">Succeeded</TH>
                  <TH align="right">Total spend</TH><TH align="right">Last payment</TH>
                </TR>
              </THead>
              <TBody>
                {data.customers.map((customer) => (
                  <TR key={customer.id}>
                    <TD>
                      {/* Phone leads, because that is what identifies them
                          and what a merchant will search by. */}
                      <div className="font-medium text-ink">
                        {customer.name || customer.phone || customer.email}
                      </div>
                      <div className="text-xs text-muted">
                        {[customer.name && customer.phone, customer.email]
                          .filter(Boolean)
                          .join(' · ') || (customer.name ? customer.phone || customer.email : '')}
                      </div>
                    </TD>
                    <TD align="right" className="tabular text-sm">{formatNumber(customer.paymentCount)}</TD>
                    <TD align="right" className="tabular text-sm">{formatNumber(customer.succeededCount)}</TD>
                    <TD align="right" className="tabular font-medium">
                      {formatMoney(customer.totalSpend, customer.currency ?? 'USD')}
                    </TD>
                    <TD align="right" className="text-xs text-muted">{formatTimestamp(customer.lastPaymentAt)}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
            <Pagination total={data.total} limit={data.limit} skip={data.skip} onChange={setSkip} />
          </>
        )}
      </Card>

      <NewCustomerDialog
        open={adding}
        onClose={() => setAdding(false)}
        onCreated={refetch}
      />
    </>
  );
}

export default CustomersPage;
